import { faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import { KeyboardEvent, ReactElement, useState } from 'react';
import { Alert, Button, Copyable } from '.';
import {
  normaliseRedirectUrl,
  redirectUrlError,
} from '../utilities/identity-providers';
import './RedirectUrlsInput.scss';

type RedirectUrlsInputProps = {
  id?: string;
  value: string[];
  onChange: (value: string[]) => void;
  disabled?: boolean;

  /**
   * Show a copy button next to each URL
   */
  copyable?: boolean;
};

/**
 * Edit an identity group's redirect URLs: the pages in the customer's app
 * that sign-ins may return to
 */
export function RedirectUrlsInput({
  id,
  value,
  onChange,
  disabled,
  copyable,
}: RedirectUrlsInputProps): ReactElement {
  const [newUrl, setNewUrl] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleAdd = () => {
    if (!newUrl.trim()) {
      return;
    }

    const problem = redirectUrlError(newUrl);

    if (problem) {
      setError(problem);
      return;
    }

    const normalised = normaliseRedirectUrl(newUrl);

    if (value.includes(normalised)) {
      setError('This URL is already in the list');
      return;
    }

    onChange([...value, normalised]);
    setNewUrl('');
    setError(null);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleAdd();
    }
  };

  return (
    <div className="redirect-urls-input">
      {value.length > 0 ? (
        <ul>
          {value.map(url => (
            <li key={url}>
              {copyable ? <Copyable text={url} /> : <code>{url}</code>}
              <Button
                icon={faTrash}
                title="Remove"
                onClick={() => onChange(value.filter(v => v !== url))}
                disabled={disabled}
                minimal
              />
            </li>
          ))}
        </ul>
      ) : (
        <p className="extra">No redirect URLs yet.</p>
      )}
      <div className="redirect-urls-input-add">
        <input
          type="url"
          id={id}
          value={newUrl}
          placeholder="https://myapp.com/auth/callback"
          disabled={disabled}
          onChange={e => {
            setNewUrl(e.target.value);
            setError(null);
          }}
          onKeyDown={handleKeyDown}
        />
        <Button
          icon={faPlus}
          label="Add"
          onClick={handleAdd}
          disabled={disabled || !newUrl.trim()}
          secondary
        />
      </div>
      <Alert intent="danger" show={!!error}>
        {error}
      </Alert>
    </div>
  );
}
